import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import {
  getGradeCorrectionRequests,
  createGradeCorrectionRequest,
  approveGradeCorrectionRequest,
  rejectGradeCorrectionRequest,
} from "../../../services/GradeCorrectionRequestService";
import { getResults } from "../../../services/ExamResultService";
import { getExams } from "../../../services/ExamService";
import { getStudentProfiles } from "../../../services/StudentProfileService";
import { getUsers } from "../../../services/UserService";
import { usePermissions, PERMISSIONS } from "../../../utils/permissions";
import { Trans, useTranslation } from "react-i18next";
import { lazyLabels } from "../../../utils/i18nLabels";

const statusLabels = lazyLabels({ pending: "pages.gradeCorrectionRequests.enAttente", approved: "pages.gradeCorrectionRequests.approuvee", rejected: "pages.gradeCorrectionRequests.rejetee" });
const statusBadge = { pending: "warning", approved: "success", rejected: "danger" };

const emptyForm = { exam_result_id: "", new_score_percentage: "", reason: "" };

const GradeCorrectionRequests = () => {
  const { t } = useTranslation();
  const currentUserId = useSelector((state) => state.auth.auth?.user?.id);
  // La demande elle-même reste ouverte à tous (un enseignant motive une
  // correction) ; seules l'approbation et le rejet — la double validation —
  // sont réservés aux comptes portant la permission correspondante.
  const can = usePermissions();
  const canCreate = can(PERMISSIONS.GRADE_CORRECTION_REQUEST_CREATE);
  const canApprove = can(PERMISSIONS.GRADE_CORRECTION_REQUEST_APPROVE);
  const canReject = can(PERMISSIONS.GRADE_CORRECTION_REQUEST_REJECT);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [results, setResults] = useState([]);
  const [exams, setExams] = useState([]);
  const [students, setStudents] = useState([]);
  const [users, setUsers] = useState([]);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [statusFilter, setStatusFilter] = useState("");

  const [showFormModal, setShowFormModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [actingId, setActingId] = useState(null);

  useEffect(() => {
    getResults({ page: 1, limit: 200 }).then((res) => setResults(res.data?.data || [])).catch(() => {});
    getExams({ page: 1, limit: 200 }).then((res) => setExams(res.data?.data || [])).catch(() => {});
    getStudentProfiles({ page: 1, limit: 200 }).then((res) => setStudents(res.data?.data || [])).catch(() => {});
    getUsers({ page: 1, limit: 200 }).then((res) => setUsers(res.data?.data || [])).catch(() => {});
  }, []);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { page, limit, sort_by: "requested_at", sort_dir: "desc" };
      if (statusFilter) params.status = statusFilter;

      const res = await getGradeCorrectionRequests(params);
      const body = res.data;
      setRequests(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      setLoadError(err?.response?.data?.message || t("pages.gradeCorrectionRequests.impossibleChargerDemandesCorrection"));
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, t]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const resultLabel = (id) => {
    const result = results.find((r) => r.id === id);
    if (!result) return "-";
    const exam = exams.find((e) => e.id === result.exam_id);
    const profile = students.find((s) => s.id === result.student_id);
    const user = profile ? users.find((u) => u.id === profile.user_id) : null;
    const studentText = user ? `${user.email} (${profile.matricule})` : profile?.matricule || "-";
    return `${exam?.title || t("common.ui.examen")} — ${studentText}`;
  };

  const resetFilters = () => {
    setStatusFilter("");
    setPage(1);
  };

  const openAddModal = () => {
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitError("");
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (submitting) return;
    setShowFormModal(false);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.exam_result_id) errors.exam_result_id = t("pages.gradeCorrectionRequests.leResultatConcerneEstRequis");
    const score = Number(formData.new_score_percentage);
    if (formData.new_score_percentage === "" || Number.isNaN(score) || score < 0 || score > 100) {
      errors.new_score_percentage = t("pages.gradeCorrectionRequests.indiquezScoreEntre0100");
    }
    if (!formData.reason.trim() || formData.reason.trim().length < 5) {
      errors.reason = t("pages.gradeCorrectionRequests.laMotivationEstObligatoire5Caracteres");
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      await createGradeCorrectionRequest({
        exam_result_id: formData.exam_result_id,
        new_score_percentage: Number(formData.new_score_percentage),
        reason: formData.reason.trim(),
      });
      setFeedback({ type: "success", message: t("pages.gradeCorrectionRequests.demandeCorrectionCreeeElleDoitMaintenant") });
      setShowFormModal(false);
      fetchRequests();
    } catch (err) {
      setSubmitError(err?.response?.data?.message || t("common.crud.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (request) => {
    setActingId(request.id);
    try {
      await approveGradeCorrectionRequest(request.id);
      const isSecond = !!request.first_approved_by;
      setFeedback({
        type: "success",
        message: isSecond
          ? t("pages.gradeCorrectionRequests.secondeValidationEffectueeNoteEteCorrigee")
          : t("pages.gradeCorrectionRequests.premiereValidationEffectueeSecondePersonneDoit"),
      });
      fetchRequests();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.gradeCorrectionRequests.impossibleValiderDemande") });
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (request) => {
    setActingId(request.id);
    try {
      await rejectGradeCorrectionRequest(request.id);
      setFeedback({ type: "success", message: t("pages.gradeCorrectionRequests.demandeCorrectionRejetee") });
      fetchRequests();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.gradeCorrectionRequests.impossibleRejeterDemande") });
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{t("pages.gradeCorrectionRequests.demandesCorrectionNote")}</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="pe-1 mb-2">
              <button type="button" className="btn btn-outline-light bg-white btn-icon me-1" title={t("common.crud.refresh")} onClick={resetFilters}>
                <i className="ti ti-refresh"></i>
              </button>
            </div>
            {canCreate && (
              <div className="mb-2">
                <button type="button" className="btn btn-primary d-flex align-items-center" onClick={openAddModal}>
                  <i className="ti ti-square-rounded-plus me-2"></i>{t("pages.gradeCorrectionRequests.nouvelleDemande")}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="alert alert-info">
          <Trans i18nKey="pages.gradeCorrectionRequests.touteCorrectionNoteApresPublicationDoit" components={[<strong />]} />
        </div>

        {feedback && (
          <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
            {feedback.message}
            <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{t("pages.gradeCorrectionRequests.listeDemandes")}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="mb-3 me-2">
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t("common.crud.allStatuses")}</option>
                  <option value="pending">{t("pages.gradeCorrectionRequests.enAttente")}</option>
                  <option value="approved">{t("pages.gradeCorrectionRequests.approuvee")}</option>
                  <option value="rejected">{t("pages.gradeCorrectionRequests.rejetee")}</option>
                </select>
              </div>
            </div>
          </div>

          <div className="card-body p-0 py-3">
            <div className="table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    <th>{t("pages.gradeCorrectionRequests.resultatConcerne")}</th>
                    <th>{t("pages.gradeCorrectionRequests.ancienneNote")}</th>
                    <th>{t("pages.gradeCorrectionRequests.nouvelleNote")}</th>
                    <th>{t("pages.gradeCorrectionRequests.motif")}</th>
                    <th>{t("pages.gradeCorrectionRequests.validations")}</th>
                    <th>{t("common.crud.statusColumn")}</th>
                    <th>{t("common.crud.actionColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{t("common.crud.loading")}</span>
                        </div>
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4 text-danger">
                        {loadError}
                      </td>
                    </tr>
                  ) : requests.length > 0 ? (
                    requests.map((request) => {
                      const validationCount = (request.first_approved_by ? 1 : 0) + (request.second_approved_by ? 1 : 0);
                      const canActOnSecond = request.first_approved_by && request.first_approved_by !== currentUserId;
                      return (
                        <tr key={request.id}>
                          <td>{resultLabel(request.exam_result_id)}</td>
                          <td>{request.old_score_percentage}%</td>
                          <td className="fw-medium">{request.new_score_percentage}%</td>
                          <td>
                            <span className="text-truncate d-inline-block" style={{ maxWidth: "260px" }} title={request.reason}>
                              {request.reason}
                            </span>
                          </td>
                          <td>{validationCount} / 2</td>
                          <td>
                            <span className={`badge badge-soft-${statusBadge[request.status] || "secondary"}`}>
                              {statusLabels[request.status] || request.status}
                            </span>
                          </td>
                          <td>
                            {request.status === "pending" && (canApprove || canReject) ? (
                              <div className="d-flex gap-2">
                                {canApprove && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-success"
                                    disabled={actingId === request.id || (request.first_approved_by && !canActOnSecond)}
                                    onClick={() => handleApprove(request)}
                                    title={
                                      request.first_approved_by && !canActOnSecond
                                        ? t("pages.gradeCorrectionRequests.laSecondeValidationDoitEtreFaite")
                                        : ""
                                    }
                                  >
                                    {actingId === request.id ? "..." : request.first_approved_by ? t("pages.gradeCorrectionRequests.valider22") : t("pages.gradeCorrectionRequests.valider12")}
                                  </button>
                                )}
                                {canReject && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    disabled={actingId === request.id}
                                    onClick={() => handleReject(request)}
                                  >
                                    {t("pages.gradeCorrectionRequests.rejeter")}
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted">{request.status === "pending" ? t("pages.gradeCorrectionRequests.enAttente") : t("pages.gradeCorrectionRequests.traitee")}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <i className="ti ti-search-off fs-1 text-muted"></i>
                        <h5 className="mt-2">{t("pages.gradeCorrectionRequests.aucuneDemandeTrouvee")}</h5>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center px-3 pt-3 border-top">
                <span className="text-sm text-gray-600">{t("pages.gradeCorrectionRequests.demande", { total })}</span>
                <div className="d-flex gap-1">
                  <button className="btn btn-sm btn-outline-secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                    {t("common.ui.precedent")}
                  </button>
                  <button className="btn btn-sm btn-outline-secondary" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                    {t("common.ui.suivant")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} centered size="lg">
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{t("pages.gradeCorrectionRequests.nouvelleDemandeCorrection")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {submitError && <div className="alert alert-danger">{submitError}</div>}
            <div className="mb-3">
              <label className="form-label">
                {t("pages.gradeCorrectionRequests.resultatConcerne")} <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${formErrors.exam_result_id ? "is-invalid" : ""}`}
                value={formData.exam_result_id}
                onChange={(e) => handleFormChange("exam_result_id", e.target.value)}
              >
                <option value="">{t("common.crud.selectPlaceholder")}</option>
                {results.map((r) => (
                  <option key={r.id} value={r.id}>
                    {resultLabel(r.id)} ({r.score_percentage}%)
                  </option>
                ))}
              </select>
              {formErrors.exam_result_id && <div className="invalid-feedback d-block">{formErrors.exam_result_id}</div>}
            </div>
            <div className="mb-3">
              <label className="form-label">
                {t("pages.gradeCorrectionRequests.nouvelleNote2")} <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                className={`form-control ${formErrors.new_score_percentage ? "is-invalid" : ""}`}
                value={formData.new_score_percentage}
                onChange={(e) => handleFormChange("new_score_percentage", e.target.value)}
              />
              {formErrors.new_score_percentage && <div className="invalid-feedback d-block">{formErrors.new_score_percentage}</div>}
            </div>
            <div className="mb-3">
              <label className="form-label">
                {t("pages.gradeCorrectionRequests.motifCorrection")} <span className="text-danger">*</span>
              </label>
              <textarea
                className={`form-control ${formErrors.reason ? "is-invalid" : ""}`}
                rows={3}
                value={formData.reason}
                onChange={(e) => handleFormChange("reason", e.target.value)}
              ></textarea>
              {formErrors.reason && <div className="invalid-feedback d-block">{formErrors.reason}</div>}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={closeFormModal} disabled={submitting}>
              {t("common.crud.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t("common.crud.saving") : t("pages.gradeCorrectionRequests.soumettreDemande")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default GradeCorrectionRequests;
